import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Alert from "@mui/material/Alert";
import Spinner from "../../components/common/Spinner";
import {
  ivrRequest,
  baseServerUrl,
  login_request,
  checkTokenValid,
  checkLoginFailure,
  TIMEOUT_MESSAGE,
  BAD_RESPONSE_MESSAGE,
  AXIOS_BAD_RESPONSE,
  AXIOS_TIMEOUT_CODE,
} from "../../constants/default_request";
import { login, logout } from "../../actions/middleware_actions";
import TitleBar from "../../components/TitleBar";
import Token from "../../components/common/RefreshToken";
import VmsCodeForm from "../../forms/IVR/VmsCode/VmsCodeForm";
import { BaseAPI } from "../../apis/BaseAPI";

function VmsCodePage({
  auth,

  username,
  apiErrors,
  userLogin,
  userLogout,
  ...props
}) {
  const location = useLocation();
  const { permitNbr } = location.state;
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [errors, setErrors] = useState({});
  const [vmsLastCode, setLastCode] = useState(null);
  const [codeMessage, setCodeMessage] = useState("");
  const [showLoading, setShowLoading] = useState(true);
  const isTokenValid = checkTokenValid(auth);
  const didLoginFail = checkLoginFailure(apiErrors);

  useEffect(() => {
    didLoginFail ? setOpenDialog(true) : setOpenDialog(false);
  }, [didLoginFail]);

  useEffect(() => {
    if (!isTokenValid) {
      setOpenDialog(true);
      return;
    }
    const request_data = ivrRequest(
      auth.client_id,
      auth.token,
      auth.user_app_status,
      permitNbr
    );
    let request_url = `${baseServerUrl}/base_api/get_last_vms_code_of_vessel`;
    if (auth.user_app_status === "TEST")
      request_url = request_url.concat("/test");

    BaseAPI.call(request_url, request_data)
      .then((response) => {
        if (response.data.status === "success") {
          setLastCode(response.data.last_vms_code);
          setCodeMessage(
            `Your last VMS declaration was ${response.data.last_vms_code}.  
            Would you like to re-declare this code?`
          );
        } else if (response.data.status === "error") {
          setCodeMessage(response.data.message);
        }
      })
      .catch((error) => {
        if (error.hasOwnProperty("code") && error.code === AXIOS_TIMEOUT_CODE) {
          setErrors({ onSubmitted: TIMEOUT_MESSAGE });
        } else if (
          error.hasOwnProperty("code") &&
          error.code === AXIOS_BAD_RESPONSE
        ) {
          setErrors({ onSubmitted: BAD_RESPONSE_MESSAGE });
        } else {
          setErrors({ onSubmitted: error.message });
        }
      })
      .finally(() => setShowLoading(false));
    // eslint-disable-next-line
  }, [isTokenValid]);

  const handleCancel = (event) => {
    event.preventDefault();
    navigate(-1);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!checkTokenValid(auth)) {
      setOpenDialog(true);
      return;
    }
    const request_data = ivrRequest(
      auth.client_id,
      auth.token,
      auth.user_app_status,
      permitNbr
    );
    let request_url = `${baseServerUrl}/base_api/evtr/v_1_vms_redeclare`;
    if (auth.user_app_status === "TEST") {
      request_url = request_url.concat("/test");
    }
    const payload = {
      ...request_data,
      form_name: "VMS REDECLARATION",
      form_version: 1,
      vessel_permit_num: permitNbr,
      vms_activity_code: vmsLastCode,
    };
    setShowLoading(true);

    BaseAPI.call(request_url, payload)
      .then((response) => {
        if (response.data.hasOwnProperty("errors")) {
          toast.error(`An error occurred: ${response.data.status}`, {
            autoClose: false,
          });
          console.log("API Error", response.data);
          setErrors({ onSubmitted: response.data.errors });
        } else {
          console.log("API Response", response.data);
          toast.success(`Submission status: ${response.data.status}`);
          navigate(-1);
        }
      })
      .catch((error) => {
        let errorMessage = "";

        if (error.hasOwnProperty("message")) {
          errorMessage = error.message;
        }

        if (error.response) {
          // client received an error response (5xx, 4xx)
          console.log("Error response", error.response, error.message);
        } else if (error.request) {
          // client never received a response, or request never left
          console.log("Bad request", error.request, error.message);
          //   saveIvrTrip(auth.client_id, actionType, request_url, payload);
        } else if (
          error.hasOwnProperty("code") &&
          error.code === AXIOS_TIMEOUT_CODE
        ) {
          errorMessage = TIMEOUT_MESSAGE;
        } else if (
          error.hasOwnProperty("code") &&
          error.code === AXIOS_BAD_RESPONSE
        ) {
          errorMessage = BAD_RESPONSE_MESSAGE;
        } else {
          // anything else
          console.log("Other error", error, error.message);
        }
        toast.error(`An error occurred.  ${errorMessage}`, {
          autoClose: false,
        });
        setErrors({ onSubmitted: errorMessage });
      })
      .finally(() => setShowLoading(false));
  };

  return (
    <div style={{ width: "100%" }}>
      <TitleBar title="Re-Declare VMS Code" />
      {openDialog && (
        <Token
          user={username}
          loginRequest={login_request}
          loginUser={userLogin}
          logoutUser={userLogout}
          toggleDialog={setOpenDialog}
        />
      )}
      {errors.hasOwnProperty("onSubmitted") && (
        <Alert severity="error">{errors.onSubmitted}</Alert>
      )}
      {showLoading ? (
        <Spinner />
      ) : (
        <VmsCodeForm
          lastCode={vmsLastCode}
          message={codeMessage}
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
        />
      )}
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  const auth = state.auth;
  const username = state.auth.username;
  const apiErrors = state.error;

  return { auth, username, apiErrors };
};

const mapDispatchToProps = {
  userLogin: login,
  userLogout: logout,
};

export default connect(mapStateToProps, mapDispatchToProps)(VmsCodePage);
